import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, X } from "lucide-react";

const WHATSAPP_URL =
  "https://wa.me/14158885550?text=" +
  encodeURIComponent("Hi Pebble Beach Luxury Ride, I'd like to request a quote.");
const BOOKING_URL = "tel:+18334575466";

const LINKS = [
  { label: "Home", href: "https://www.pebblebeachluxuryride.com/index.html" },
  { label: "Services", href: "https://www.pebblebeachluxuryride.com/services.html" },
  { label: "Fleet", href: "#fleet" },
  { label: "Golf", href: "https://www.pebblebeachluxuryride.com/golf.html" },
  { label: "Events", href: "https://www.pebblebeachluxuryride.com/events.html" },
  { label: "Airports", href: "https://www.pebblebeachluxuryride.com/airport-transfers.html" },
  { label: "Long Distance", href: "https://www.pebblebeachluxuryride.com/long-distance.html" },
  { label: "About", href: "https://www.pebblebeachluxuryride.com/about.html" },
  { label: "Contact", href: "https://www.pebblebeachluxuryride.com/contact.html" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-onyx/80 backdrop-blur-xl border-b border-line" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <a href="#top" data-testid="brand-logo" className="group flex items-center gap-3">
          <img
            src="/images/emblem.png"
            alt="Pebble Beach Luxury Ride — Lone Cypress emblem"
            className="h-9 w-auto"
          />
          <span className="flex flex-col leading-none">
            <span className="font-serif text-xl tracking-tight text-bone sm:text-2xl">
              Pebble Beach <span className="italic text-champagne">Luxury Ride</span>
            </span>
            <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
              Chauffeur &amp; Fleet
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-4 xl:flex" data-testid="desktop-nav">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke transition-colors duration-300 hover:text-champagne"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-button"
            className="flex items-center gap-2 border border-champagne/40 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-champagne transition-colors duration-300 hover:bg-champagne hover:text-onyx"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            WhatsApp
          </a>
          <a
            href={BOOKING_URL}
            data-testid="nav-booking-button"
            className="bg-champagne px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-onyx transition-colors duration-300 hover:bg-champagne-light"
          >
            Book / Quote
          </a>
        </div>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          className="text-bone xl:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="border-b border-line bg-onyx/95 backdrop-blur-xl xl:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-5 px-6 py-6">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-mono text-xs uppercase tracking-[0.25em] text-smoke"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex gap-3 pt-2">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 border border-champagne/40 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-champagne"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
                <a
                  href={BOOKING_URL}
                  className="bg-champagne px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-onyx"
                >
                  Book / Quote
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
